import { Button, Caption, Card, Column, Heading, IconButton, Link, Row } from "@gadgetinc/widgets";
import { BackArrowIcon } from "@gadgetinc/widgets/src/icons/BackArrowIcon";
import { EditIcon } from "@gadgetinc/widgets/src/icons/EditIcon";
import { InfoIcon } from "@gadgetinc/widgets/src/icons/InfoIcon";
import { useStyletron } from "baseui";
import { capitalize, truncate } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useCallback, useMemo, useState } from "react";
import { AppTemplateType, WebAppAuthType } from "state-trees/src/AppTemplateType";
import { ShopifyAppType } from "state-trees/src/assistant/AssistantMetadata";
import {
  appTemplateTypeToAppClassificationType,
  shopifyAppTypeToAuthClassificationType,
  webAppAuthTypeToAuthClassificationType,
} from "state-trees/src/assistant/summon";
import { ChatGPTAuthOptions, WebAppAuthOptions } from "../../../client-state-trees/AppTemplates";
import { AppSummonSession } from "../../../client-state-trees/auth/AppSummonSession";
import { useFadeIn } from "../../../lib/hooks";
import { safelyRunAsyncEventHandler } from "../../../lib/utils";
import { useClearUnfinishedSummon } from "../../auth/summon/useClearUnfinishedSummon";
import { BoundInput } from "../../bound-controls/BoundInput";
import { BoundTextArea } from "../../bound-controls/BoundTextArea";
import { AppSummonMessage } from "../../edit/workspace/bootloader/AppSummonMessage";
import { AppSummonBootLoaderShell } from "../../edit/workspace/bootloader/EditBootloaderSkeleton";
import { FormField } from "../FormField";
import { AppTemplateRadioGroup, ShopifyAppTypeRadioGroup, useNewAppTemplates, WebAppAuthTypeRadioGroup } from "../NewAppPage";
import { ClickToEditText, InfoCaption } from "./ClickToEditText";

export const SummonAppChat = observer((props: { session: AppSummonSession; userEmailHash: string }) => {
  const { session, userEmailHash } = props;

  if (session.appSummaryState !== "completed") {
    return <SummonAppConfirmDetails session={session} />;
  }

  return (
    <AppSummonBootLoaderShell
      fadeIn
      loading
      prompt={session.prompt}
      user={userEmailHash}
      systemMessage={session.systemMessage}
      appSummary={
        <AppSummonMessage
          prompt={session.prompt}
          summary={session.summary ?? ""}
          appName={session.appSlug ?? ""}
          appTemplateType={session.appTemplateType!}
          shopifyAppType={session.shopifyAppType}
        />
      }
      settingUpMessage={session.settingUpMessage}
      settingUpSteps={session.settingUpSteps.map((step) => ({ step: step.message, completed: step.completed }))}
    />
  );
});

const SummonAppConfirmDetails = observer((props: { session: AppSummonSession }) => {
  const { session } = props;
  const [_, $theme] = useStyletron();
  const { clearUnfinishedSummon } = useClearUnfinishedSummon();
  const summonKey = session.key;

  const confirmDetails = useCallback(() => {
    safelyRunAsyncEventHandler(async () => {
      const valid = await session.validate();
      if (!valid) {
        return;
      }
      session.setFastMode(true);
      session.setAppSummaryState("completed");
      await clearUnfinishedSummon(summonKey);
    });
  }, [session, summonKey, clearUnfinishedSummon]);

  return (
    <Column $style={{ ...useFadeIn(), width: "600px", margin: "0 auto", gap: $theme.sizing.scale600 }}>
      <Row>
        <IconButton
          label=""
          onClick={() => {
            session.reset();
          }}
        >
          <BackArrowIcon />
        </IconButton>
        <Heading $style={{ marginLeft: $theme.sizing.scale300, fontSize: $theme.sizing.scale700 }}>App details</Heading>
      </Row>

      <AppNameConfig session={session} />
      <PromptSummaryConfig session={session} />
      <AppTypeSelector session={session} />

      <Row $style={{ flex: 1, justifyContent: "flex-end" }}>
        <Button
          data-testid="summon-app-confirm-details-button"
          isLoading={session.validating}
          onClick={confirmDetails}
          disabled={!session.appSlug || session.appSlug.length === 0}
        >
          {"Confirm"}
        </Button>
      </Row>
    </Column>
  );
});

const AppNameConfig = observer((props: { session: AppSummonSession }) => {
  const { session } = props;

  return (
    <Column $style={{ maxWidth: "50%" }}>
      <FormField label="Name" error={session.invalidNameError ? capitalize(session.invalidNameError) : undefined}>
        <BoundInput node={session} property="appSlug" placeholder="my-app" maxLength={54} dataTestId="summon-app-slug-input" />
      </FormField>
    </Column>
  );
});
const PromptSummaryConfig = observer((props: { session: AppSummonSession }) => {
  const { session } = props;
  const [_, $theme] = useStyletron();
  const [editing, setEditing] = useState(false);

  const shownSummary = truncate(session.summary ?? "", { length: 500 });

  return (
    <Column>
      <Heading $style={{ marginBottom: $theme.sizing.scale200 }}>Prompt summary</Heading>

      {editing ? (
        <>
          <FormField>
            <Row>
              <BoundTextArea
                dataTestId="app-description"
                autoFocus
                node={session}
                property="summary"
                overrides={{ Input: { style: { height: "124px" } } }}
              />
            </Row>
          </FormField>
        </>
      ) : (
        <Column $gap={$theme.sizing.scale300}>
          <ClickToEditText
            text={shownSummary}
            onEdit={() => {
              setEditing(true);
            }}
          />

          <InfoCaption info={"We've optimized your prompt for best results."} />
        </Column>
      )}
    </Column>
  );
});

const AppTypeSelector = observer((props: { session: AppSummonSession }) => {
  const { session } = props;
  const [_, $theme] = useStyletron();

  const [editing, setEditing] = useState(false);

  const canEditAppType = session.appTemplateType && !session.hideAppType;
  const canEditShopifyAuthType = session.appType === "Shopify" && !session.hideAuthType;
  const canEditWebAuthType = session.appType === "Web" && !session.hideAuthType;
  const canEditAuthType = canEditShopifyAuthType || canEditWebAuthType;

  const canEdit = canEditAppType || canEditAuthType;

  const NewAppTemplates = useNewAppTemplates();

  const currentAppTemplate = session.appType && NewAppTemplates.find((appTemplate) => appTemplate.value.startsWith(session.appType!));

  if (!canEdit || !currentAppTemplate) {
    return null;
  }

  return (
    <Column>
      <Heading $style={{ marginBottom: $theme.sizing.scale500 }}>App type</Heading>

      {!editing && (
        <Card $style={{ backgroundColor: "transparent", padding: $theme.sizing.scale500 }}>
          <Row $style={{ alignItems: "center", gap: $theme.sizing.scale300 }}>
            {currentAppTemplate.icon}

            <Column $style={{ marginRight: "auto" }}>
              {currentAppTemplate.title}
              <AppAuthTypeCaption session={session} />
            </Column>
            <IconButton
              label="Edit"
              onClick={() => {
                setEditing(true);
              }}
              $style={{ marginLeft: "auto" }}
            >
              <EditIcon />
            </IconButton>
          </Row>
        </Card>
      )}

      {editing && (
        <Card $style={{ backgroundColor: "transparent" }}>
          <Column $gap={$theme.sizing.scale600}>
            {canEditAppType && (
              <AppTemplateRadioGroup
                value={
                  [AppTemplateType.WebAppWithAuth, AppTemplateType.InternalTool, AppTemplateType.Empty].includes(session.appTemplateType)
                    ? AppTemplateType.WebAppWithAuth
                    : session.appTemplateType
                }
                onChange={(value) => {
                  session.setAppType(appTemplateTypeToAppClassificationType(value) ?? "Web");
                }}
                appTemplateTypes={NewAppTemplates.filter((appTemplate) =>
                  [AppTemplateType.ShopifyApp, AppTemplateType.WebAppWithAuth].includes(appTemplate.value)
                )}
              />
            )}
            {canEditShopifyAuthType && (
              <ShopifyAppTypeRadioGroup
                value={session.shopifyAppType ?? ShopifyAppType.Public}
                onChange={(value) => {
                  session.setAuthType(shopifyAppTypeToAuthClassificationType(value) ?? "Multi tenant");
                }}
              />
            )}
            {canEditWebAuthType && (
              <WebAppAuthTypeRadioGroup
                value={session.webAppAuthType ?? WebAppAuthType.EmailPassword}
                onChange={(value) => {
                  session.setAuthType(webAppAuthTypeToAuthClassificationType(value) ?? "Multi tenant");
                }}
                appTemplateType={session.appTemplateType}
              />
            )}
          </Column>
        </Card>
      )}

      {session.appType === "Shopify" && <ShopifyAppsNeedPartnersAccountCaption />}
    </Column>
  );
});

const AppAuthTypeCaption = observer((props: { session: AppSummonSession }) => {
  const { session } = props;

  const content = useMemo(() => {
    if (session.appType === "Shopify") {
      if (session.authType === "Single tenant") {
        return "Custom app";
      }
      if (session.authType === "Multi tenant") {
        return "Public app";
      }
    }
    if (session.appType === "Web") {
      // Use ChatGPTAuthOptions for ChatGPT apps, otherwise use WebAppAuthOptions
      const authOptions = session.appTemplateType === AppTemplateType.ChatGPTApp ? ChatGPTAuthOptions : WebAppAuthOptions;
      return session.webAppAuthType && authOptions[session.webAppAuthType as keyof typeof authOptions]?.title;
    }
  }, [session.appType, session.authType, session.appTemplateType, session.webAppAuthType]);

  if (!content) {
    return null;
  }

  return <Caption>{content}</Caption>;
});

const ShopifyAppsNeedPartnersAccountCaption = () => {
  const [_, $theme] = useStyletron();

  return (
    <Row $style={{ alignItems: "center", gap: $theme.sizing.scale100, marginTop: $theme.sizing.scale300 }}>
      <InfoIcon />
      <Caption>
        {"Shopify apps will require you to set up a "}
        <Link href="https://partners.shopify.com" target="_blank" endEnhancer={false}>
          Shopify Partners
        </Link>
        {"account"}
      </Caption>
    </Row>
  );
};
